import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import {
  AlertCircle, FileText, Loader2, RefreshCw, Upload, X,
} from 'lucide-react';

const fmtDate = (v) => {
  if (!v) return '\u2014';
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const fmtBytes = (n) => {
  if (!n) return '\u2014';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

export default function EmergencyBillUpload() {
  const [currentAccount, setCurrentAccount] = useState(() => {
    const s = localStorage.getItem('current_account');
    return s ? JSON.parse(s) : null;
  });
  useEffect(() => {
    const check = () => {
      const s = localStorage.getItem('current_account');
      const acc = s ? JSON.parse(s) : null;
      if (JSON.stringify(acc) !== JSON.stringify(currentAccount)) setCurrentAccount(acc);
    };
    const h = setInterval(check, 800);
    window.addEventListener('storage', check);
    return () => { clearInterval(h); window.removeEventListener('storage', check); };
  }, [currentAccount]);
  const accountId = currentAccount?.id;

  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchUploads = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/payments/v2/emergency-bill-upload?account_id=${accountId}`);
      setUploads(data.uploads || []);
    } catch (e) {
      toast.error('Failed to load uploads');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { fetchUploads(); }, [fetchUploads]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!accountId) {
      toast.error('No account selected');
      return;
    }
    const nonPdf = files.filter((f) => !f.name.toLowerCase().endsWith('.pdf'));
    if (nonPdf.length) {
      toast.error(`Skipped ${nonPdf.length} non-PDF file(s)`);
    }
    const pdfs = files.filter((f) => f.name.toLowerCase().endsWith('.pdf'));
    if (!pdfs.length) {
      e.target.value = '';
      return;
    }

    setUploading(true);
    const tid = toast.loading(`Uploading ${pdfs.length} bill${pdfs.length > 1 ? 's' : ''}...`);
    try {
      const fd = new FormData();
      pdfs.forEach((f) => fd.append('files', f));
      const { data } = await api.post(
        `/payments/v2/emergency-bill-upload?account_id=${accountId}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const ok = (data.uploaded || []).filter((u) => u.bill_id || ['uploaded', 'accepted_by_engine', 'uploaded_pending_lookup'].includes(u.status)).length;
      const failed = (data.uploaded || []).filter((u) => u.status === 'error' || u.status === 'skipped').length;
      toast.success(`Upload complete: ${ok} succeeded${failed ? `, ${failed} failed` : ''}`, { id: tid });
      await fetchUploads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed', { id: tid });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <MainLayout>
      <div className="container mx-auto px-6 md:px-12 py-6 space-y-6" data-testid="emergency-bill-upload-page">
        {/* Temporary work-in-progress banner — remove once SOPs + automated
            payments process are live. See ROADMAP.md "Disable WIP note on
            Emergency Payments Upload". */}
        <div
          data-testid="emergency-wip-banner"
          className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600" />
          <div>
            <p className="font-semibold">This page is a work in progress.</p>
            <p className="text-amber-800">
              We're finalising the SOPs and the automated payments process. Until then, expect manual reconciliation
              after each upload — your BillPortal contact will confirm once each batch lands cleanly.
            </p>
          </div>
        </div>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Emergency Paid Bill Upload
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Upload PDFs of bills that were paid outside the platform. Files are sent to BillPortal for parsing
              and the resulting Bill ID will appear below.
            </p>
            {currentAccount && (
              <p className="text-xs text-muted-foreground mt-1">
                Customer: <span className="font-mono font-medium">{currentAccount.organisation_name}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchUploads}
              disabled={loading}
              data-testid="ebu-refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              multiple
              className="hidden"
              onChange={handleFileSelect}
              data-testid="ebu-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || !accountId}
              data-testid="ebu-upload-btn"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              {uploading ? 'Uploading...' : 'Upload Bills'}
            </Button>
          </div>
        </div>

        {!accountId && (
          <div className="border border-yellow-200 bg-yellow-50 p-4 rounded-none flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-800">
              No account selected. Please select an organisation from the top-right account switcher.
            </p>
          </div>
        )}

        {/* Uploads Table */}
        <div className="border border-border rounded-none bg-card">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-medium uppercase tracking-wide">Uploaded Bills</h2>
            <span className="text-xs text-muted-foreground" data-testid="ebu-count">
              {uploads.length} record{uploads.length === 1 ? '' : 's'}
            </span>
          </div>
          <Table data-testid="ebu-table">
            <TableHeader>
              <TableRow>
                <TableHead>Bill ID</TableHead>
                <TableHead>File Name</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Engine</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead>Created By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && uploads.length === 0 && (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-24" /></TableCell>
                    ))}
                  </TableRow>
                ))
              )}
              {!loading && uploads.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-sm text-muted-foreground">
                    No uploads yet. Click "Upload Bills" to add emergency paid bills.
                  </TableCell>
                </TableRow>
              )}
              {uploads.map((u) => (
                <TableRow key={u.id} data-testid={`ebu-row-${u.id}`}>
                  <TableCell className="font-mono text-xs">
                    {u.bill_id || (
                      <span className="text-muted-foreground italic">pending lookup</span>
                    )}
                  </TableCell>
                  <TableCell className="max-w-xs truncate" title={u.file_name}>
                    <div className="flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="truncate">{u.file_name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">{fmtBytes(u.file_size)}</TableCell>
                  <TableCell data-testid={`ebu-engine-status-${u.id}`}>
                    {u.status === 'accepted_by_engine' && <Badge variant="default">Accepted</Badge>}
                    {u.status === 'uploaded' && <Badge variant="default">Uploaded</Badge>}
                    {u.status === 'uploaded_pending_lookup' && <Badge variant="secondary">Processing</Badge>}
                    {u.status === 'error' && <Badge variant="destructive">Error</Badge>}
                    {u.status === 'skipped' && <Badge variant="outline">Skipped</Badge>}
                  </TableCell>
                  <TableCell data-testid={`ebu-payment-status-${u.id}`}>
                    {u.payment_status === 'paid' && (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                        Paid {u.paid_amount != null ? `· $${Number(u.paid_amount).toFixed(2)}` : ''}
                      </Badge>
                    )}
                    {(u.payment_status === 'unpaid' || u.payment_status === 'pending' || !u.payment_status) && u.bill_id && (
                      <Badge variant="outline" className="text-muted-foreground">
                        {u.payment_status === 'unpaid' ? 'Unpaid' : 'Pending'}
                      </Badge>
                    )}
                    {!u.bill_id && (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{fmtDate(u.uploaded_at)}</TableCell>
                  <TableCell className="text-xs">{u.uploaded_by || '\u2014'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </MainLayout>
  );
}
